import type { RefObject } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Settings, CreditCard, Store, LogOut } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { User } from '@/types';

interface SidebarUserMenuProps {
  userMenuRef: RefObject<HTMLDivElement>;
  userMenuOpen: boolean;
  setUserMenuOpen: (open: boolean) => void;
  user: User | null;
  initials: string;
  onNavigateSettings: () => void;
  onNavigateBilling: () => void;
  onLogout: () => void;
}

export function SidebarUserMenu({
  userMenuRef,
  userMenuOpen,
  setUserMenuOpen,
  user,
  initials,
  onNavigateSettings,
  onNavigateBilling,
  onLogout,
}: SidebarUserMenuProps) {
  const { t } = useTranslation('dashboard');
  return (
    <div className="relative border-t border-border px-2 py-2" ref={userMenuRef}>
      <button
        type="button"
        onClick={() => setUserMenuOpen(!userMenuOpen)}
        className="w-full flex items-center gap-2.5 px-2 py-2 rounded-control hover:bg-muted transition-colors"
      >
        <div className="w-7 h-7 rounded-full bg-foreground text-background flex items-center justify-center text-[10px] font-semibold flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0 text-left">
          <p className="text-xs font-medium text-foreground truncate">{user?.name || t('shell.userFallback')}</p>
          <p className="text-[10px] text-muted-foreground truncate">{user?.email || ''}</p>
        </div>
        <ChevronDown className={cn('h-3 w-3 text-muted-foreground transition-transform', userMenuOpen && 'rotate-180')} />
      </button>

      {userMenuOpen && (
        <div className="absolute bottom-full left-2 right-2 mb-1 bg-card rounded-control shadow-lg border border-border py-1 z-50">
          <button
            type="button"
            onClick={onNavigateSettings}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-foreground hover:bg-muted"
          >
            <Settings className="h-3.5 w-3.5 text-muted-foreground" /> {t('shell.nav.settings')}
          </button>
          <button
            type="button"
            onClick={onNavigateBilling}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-foreground hover:bg-muted"
          >
            <CreditCard className="h-3.5 w-3.5 text-muted-foreground" /> {t('shell.menu.billing')}
          </button>
          <NavLink
            to="/"
            onClick={() => setUserMenuOpen(false)}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-foreground hover:bg-muted"
          >
            <Store className="h-3.5 w-3.5 text-muted-foreground" /> {t('shell.backToStore')}
          </NavLink>
          <div className="my-1 border-t border-border" />
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-xs text-plum hover:bg-bloom-pale"
          >
            <LogOut className="h-3.5 w-3.5" /> {t('shell.menu.logout')}
          </button>
        </div>
      )}
    </div>
  );
}
